/**
 * LocaleProvider — the one place where a language change becomes visible.
 *
 * Owns everything that has to move together when the language changes: the i18next instance, the
 * document's `dir`/`lang`, antd's direction and locale, the Arabic webfont, dayjs's locale, and
 * the formatting core's active locale. Splitting these across call sites is how apps end up with
 * an RTL layout rendering LTR dates.
 *
 * It also owns the antd `ConfigProvider`, because `direction` and the Arabic font token belong to
 * the same provider as the brand theme.
 *
 * Design: docs/43-I18N-ARABIC-DESIGN.md §4, §7, §8.
 */

import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ConfigProvider, type ThemeConfig } from 'antd';
import type { Locale } from 'antd/es/locale';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { buildAntdTheme } from '../theme/antdTheme';
import { renderNoData } from '../components/NoData';
import { useResolvedTheme } from '../theme/themeMode';
import { isUiV2 } from '../theme/uiFlags';
import { changeI18nLocale, initI18n } from './index';
import { t } from './t';
import { registerArabicDayjsLocale } from './dayjsArabic';
import { setActiveFormattingLocale, setHijriDisplay } from './format';
import {
  bootLocale,
  readStoredUserLocale,
  resolveEffectiveLocale,
  selectableLocales,
  writeCachedOrgLocale,
  writeStoredUserLocale,
  type OrgLocaleSettings,
} from './orgLocaleSettings';
import { DEFAULT_LOCALE, LOCALES, type Direction, type LocaleCode } from './locales';

interface LocaleContextValue {
  locale: LocaleCode;
  direction: Direction;
  isRtl: boolean;
  /** Languages this user may pick between. One entry means the switcher hides itself. */
  availableLocales: LocaleCode[];
  orgSettings: OrgLocaleSettings | null;
  /** A user's personal choice. Persisted per-device; ignored if the org forbids overrides. */
  setUserLocale: (locale: LocaleCode) => void;
  /** Called by AppContext once the org's configuration is known. */
  applyOrgSettings: (settings: OrgLocaleSettings) => void;
  /**
   * Called by AppContext on logout. Drops the previous session's org config so
   * `availableLocales` falls back to "no org has an opinion yet" (every supported
   * language) instead of quietly keeping the last org's language restrictions —
   * which otherwise left the switcher hidden on the post-logout Login screen for
   * any org with only one language enabled or with personal overrides turned off.
   */
  resetOrgSettings: () => void;
  /** False until i18next has its core namespaces — gates first paint. */
  isReady: boolean;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
};

/**
 * Writing direction for SHARED components that may render outside <LocaleProvider> (unit tests
 * that mount a screen bare, portals). Falls back to the document's `dir`, which the provider keeps
 * in step, rather than throwing like useLocale() does.
 */
export const useIsRtl = (): boolean => {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx.isRtl;
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
};

/**
 * antd ships exactly one Arabic locale, `ar_EG`. Its name is Egyptian but its strings are MSA, so
 * it is correct for the UAE; the handful of terms we render differently come from our own
 * catalogue, which takes precedence wherever we own the text.
 *
 * Imported lazily so an English-only tenant never downloads it.
 */
const loadAntdLocale = async (locale: LocaleCode): Promise<Locale | undefined> => {
  if (locale === DEFAULT_LOCALE) return undefined;
  const mod = await import('antd/locale/ar_EG');
  return mod.default;
};

/**
 * The Arabic webfont — self-hosted, and only the Arabic subset, only when Arabic is chosen.
 *
 * Poppins (the brand face) has no Arabic coverage at all, so without this the browser substitutes
 * a system font and the Arabic screens look broken beside the Latin ones. Loading the subset
 * rather than the full family keeps an English tenant's bundle unchanged.
 */
let arabicFontLoaded = false;
const loadArabicFont = async (): Promise<void> => {
  if (arabicFontLoaded) return;
  arabicFontLoaded = true;
  await Promise.all([
    import('@fontsource/ibm-plex-sans-arabic/arabic-400.css'),
    import('@fontsource/ibm-plex-sans-arabic/arabic-500.css'),
    import('@fontsource/ibm-plex-sans-arabic/arabic-600.css'),
  ]);
};

const globalSpinIndicator = <LoadingOutlined spin />;

export const LocaleProvider = ({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** Resolved in `main.tsx` before the app's module graph is imported. */
  initialLocale?: LocaleCode;
}) => {
  const [orgSettings, setOrgSettings] = useState<OrgLocaleSettings | null>(null);
  const [locale, setLocale] = useState<LocaleCode>(
    () => initialLocale ?? bootLocale(navigator.language),
  );
  const [antdLocale, setAntdLocale] = useState<Locale | undefined>(undefined);
  const [isReady, setIsReady] = useState(false);
  const bootedRef = useRef(false);

  // ── Boot ───────────────────────────────────────────────────────────────────
  // Pre-login there is no org configuration, so the locale comes from a stored personal choice or
  // the browser. Getting this right is what stops a flash of English on an Arabic tenant's login
  // screen — and what lets an Arabic speaker read the login form at all.
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    void (async () => {
      await applyLocaleSideEffects(locale);
      await initI18n(locale);
      setIsReady(true);
    })();
     
  }, []);

  /** Everything outside React that has to change when the language changes. */
  const applyLocaleSideEffects = useCallback(async (next: LocaleCode) => {
    const def = LOCALES[next];

    if (def.direction === 'rtl') {
      await loadArabicFont();
      registerArabicDayjsLocale();
    }

    // dayjs is global state, so this must happen before anything re-renders with a date in it.
    dayjs.locale(def.dayjsLocale);
    setActiveFormattingLocale(next);

    document.documentElement.setAttribute('lang', def.code);
    document.documentElement.setAttribute('dir', def.direction);

    // Some of the app's CSS keys off body rather than html; keeping both in step avoids a
    // half-mirrored layout that is very hard to diagnose from a screenshot.
    document.body.setAttribute('dir', def.direction);

    setAntdLocale(await loadAntdLocale(next));
  }, []);

  /**
   * Change language by reloading.
   *
   * Swapping catalogues in place is not enough: module-scope `t()` calls (antd column definitions,
   * the sidebar's `MODULE_META`, status maps) were evaluated once at import and cannot be
   * re-evaluated by any amount of re-rendering. A reload re-imports the module graph against the
   * new language, which is the only way those become correct.
   *
   * The caller has already persisted the choice, so boot resolves the new language immediately.
   * Changing language is a deliberate, rare action; a reload is an honest price for a UI that is
   * actually, entirely in the chosen language.
   */
  const switchTo = useCallback(
    (next: LocaleCode) => {
      if (next === locale) return;
      window.location.reload();
    },
    [locale],
  );

  // ── Org configuration arrives after sign-in ────────────────────────────────
  const applyOrgSettings = useCallback(
    (settings: OrgLocaleSettings) => {
      setOrgSettings(settings);
      setHijriDisplay(settings.showHijriDates);

      const stored = readStoredUserLocale();
      const effective = resolveEffectiveLocale(settings, stored, navigator.language);

      // Reclaim a stored choice the org no longer permits — otherwise a user who once picked
      // Arabic keeps it after the org withdraws the language, and no admin action reaches them.
      if (stored && effective !== stored) writeStoredUserLocale(null);

      // Cache the ORG DEFAULT — not the effective locale — so the next boot can resolve before the
      // module graph loads. Without a cache the reload below would resolve English again and
      // bounce forever; caching the *effective* value instead would leave a stale personal choice
      // behind after someone clears their override, costing an extra reload to correct.
      writeCachedOrgLocale(settings.defaultLocale);

      switchTo(effective);
    },
    [switchTo],
  );

  /**
   * Drop the previous session's org config on logout, so `availableLocales` (below)
   * falls back to "no org has an opinion yet" — every supported language — same as a
   * fresh, never-logged-in visit to /login. No reload here: unlike a language change,
   * this doesn't need to re-evaluate any module-scope `t()` calls, and forcing one on
   * every logout would be a jarring, unnecessary flash.
   */
  const resetOrgSettings = useCallback(() => {
    setOrgSettings(null);
  }, []);

  const setUserLocale = useCallback(
    (next: LocaleCode) => {
      if (orgSettings && !orgSettings.allowUserOverride) return;
      writeStoredUserLocale(next);
      void switchTo(next);
    },
    [orgSettings, switchTo],
  );

  /**
   * The browser tab title.
   *
   * `index.html` hard-codes it in English, so it stayed English in an otherwise fully Arabic app —
   * visible on every tab, bookmark and window-switcher entry, and easy to miss because it lives
   * outside the React tree entirely.
   *
   * It has to run **after** i18next is ready, not alongside the other side effects: those run
   * during boot, before `initI18n` resolves, so `t()` would still be falling back to English and
   * the title would never correct itself for a user whose stored locale is already Arabic.
   */
  useEffect(() => {
    if (!isReady) return;
    document.title = t('shell:document.title');
  }, [isReady, locale]);

  const direction = LOCALES[locale].direction;
  const resolvedTheme = useResolvedTheme();

  // The UI-v2 switch is fixed for the page's lifetime (resolved in main.tsx before render), so it
  // does not need to be a dependency here.
  const theme: ThemeConfig = useMemo(() => buildAntdTheme(direction === 'rtl', isUiV2(), resolvedTheme === 'dark'), [direction, resolvedTheme]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      direction,
      isRtl: direction === 'rtl',
      // `null` pre-login means "no org has an opinion yet", and `selectableLocales` answers that
      // with every supported language — which is what the sign-in screen needs. Passing the
      // English-only default here instead would hide the switcher on the one screen where an
      // Arabic speaker most needs it.
      availableLocales: selectableLocales(orgSettings),
      orgSettings,
      setUserLocale,
      applyOrgSettings,
      resetOrgSettings,
      isReady,
    }),
    [locale, direction, orgSettings, setUserLocale, applyOrgSettings, resetOrgSettings, isReady],
  );

  return (
    <LocaleContext.Provider value={value}>
      <ConfigProvider
        theme={theme}
        direction={direction}
        locale={antdLocale}
        spin={{ indicator: globalSpinIndicator }}
        renderEmpty={renderNoData}
        getPopupContainer={() => document.body}
      >
        {/*
          Remount the whole tree when the language changes.

          Most call sites use the module-level `t` rather than the `useTranslation` hook — see
          src/i18n/t.ts for why — so React has no way to know a language change affects them. The
          key forces a fresh mount, which re-evaluates every render path and every module-scope
          structure rebuilt during render.

          The cost is that in-flight component state is discarded on a language switch. That is
          acceptable and arguably correct: changing language is a deliberate, rare action, and a
          half-filled form rendered in two languages at once would be worse.
        */}
        <Fragment key={locale}>{children}</Fragment>
      </ConfigProvider>
    </LocaleContext.Provider>
  );
};

export default LocaleProvider;
